import Image from 'next/image';
import { useTranslations } from 'next-intl';
import type { ReactNode, Ref } from 'react';
import { TrackedAnchor } from '@/components/ancher/tracked-anchor';
import { TRACKED_EVENT_POSITION } from '@/constants/gtag';
import { customRichTextTags } from '../main-responsive';
import { HIGHLIGHT_ITEMS } from '../highlight.constants';

// 모바일(highlight-mobile.tsx)·데스크톱 스크럽·라이트모드 폴백이 모두 공유한다.
export function HighlightContent({
  media,
  activeCount,
  listRef,
}: {
  media: ReactNode;
  activeCount: number;
  listRef?: Ref<HTMLUListElement>;
}) {
  const t = useTranslations('Main.highlight');
  return (
    <div className="flex min-h-[inherit] flex-col items-center justify-center gap-10 bg-[#0d0e10] px-5 py-20 [--content-height:648px] [--media-size:504px] xl:gap-20 xl:px-6 xl:py-[clamp(24px,calc((var(--scroll-stage-height,100svh)-var(--content-height))/2),180px)] [@media(min-width:1200px)_and_(max-height:900px)]:gap-8 [@media(min-width:1200px)_and_(max-height:900px)]:[--content-height:520px] [@media(min-width:1200px)_and_(max-height:900px)]:[--media-size:425.25px]">
      <h2 className="text-center text-[24px] font-semibold leading-8 tracking-[-0.464px] xl:text-[48px] xl:leading-[56px] xl:tracking-[-1.072px]">
        {t.rich('title', {
          number: chunks => <span className="font-medium">{chunks}</span>,
          highlight: chunks => (
            <span className="bg-[#3992ff]/25 box-decoration-clone px-[5px] py-1">
              {chunks}
            </span>
          ),
        })}
      </h2>
      <div className="grid w-full max-w-[1260px] grid-cols-[minmax(0,676px)] items-center justify-center gap-10 xl:grid-cols-[minmax(0,var(--media-size))_minmax(0,676px)] xl:gap-[clamp(24px,4.167vw,80px)]">
        <div className="aspect-[504/512] w-full max-w-[240px] justify-self-center bg-[#0d0e10] xl:max-w-none [&>img]:h-full [&>img]:w-full [&>img]:object-contain [&>img]:mix-blend-screen [&>video]:h-full [&>video]:w-full [&>video]:object-contain [&>video]:mix-blend-screen">
          {media}
        </div>
        <ul ref={listRef} className="px-6 xl:px-2.5">
          {HIGHLIGHT_ITEMS.map((item, index) => (
            <li
              key={item}
              className="border-t border-white/[0.12] last:border-b"
            >
              <TrackedAnchor
                href={t(`${item}.href`)}
                target="_blank"
                label={`main-highlight_${item}`}
                position={TRACKED_EVENT_POSITION.MAIN_CONTENT}
                className="group relative grid min-h-[100px] grid-cols-[minmax(0,1fr)_17px] items-center gap-x-4 gap-y-1.5 py-4 before:pointer-events-none before:absolute before:-inset-x-2.5 before:inset-y-0 before:rounded-lg before:content-[''] hover:before:bg-white/[0.08] focus-visible:rounded-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#91b2ff] focus-visible:before:bg-white/[0.08] xl:min-h-[127px] xl:grid-cols-[123px_minmax(0,1fr)_30px] xl:gap-5 xl:px-3 xl:py-5 xl:before:rounded-xl [@media(min-width:1200px)_and_(max-height:900px)]:min-h-[107px]"
                data-active={index < activeCount}
              >
                <span className="text-[16px] font-semibold leading-[26px] tracking-[-0.176px] group-data-[active=false]:text-[#6c7380] group-data-[active=false]:opacity-50 xl:text-[24px] xl:leading-8 xl:tracking-[-0.464px]">
                  {t(`${item}.name`)}
                </span>
                <span className="col-start-1 break-keep text-[14px] font-normal leading-[22px] tracking-[-0.128px] text-3b-text-hint group-data-[active=false]:text-[#4a4f5a] group-data-[active=false]:opacity-50 xl:col-auto xl:text-[16px] xl:font-medium xl:leading-6 xl:tracking-[-0.176px]">
                  {t.rich(`${item}.description`, customRichTextTags)}
                </span>
                <Image
                  src="/images/main/icons/arrow-right-highlight-a.svg"
                  width={30}
                  height={14}
                  alt=""
                  className="col-start-2 row-start-1 row-end-3 w-[17px] group-data-[active=false]:opacity-50 motion-reduce:transition-none xl:col-auto xl:row-auto xl:w-[30px] xl:transition-transform xl:duration-150 xl:ease-out xl:group-hover:translate-x-1 xl:group-focus-visible:translate-x-1"
                />
              </TrackedAnchor>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
